import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getWalletBalancesAction } from 'domain/paySettings';

import useDispatchAction from 'hooks/useDispatchAction';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Skeleton from '../Skeleton';

import { getFirstColumnKey } from './helpers';

import { StyledHeaderTableCell, StyledTableContainer, StyledTableRow } from './StyledComponents';

interface WalletBalancesProps {
  id: string;
}

const WalletBalances: React.FC<WalletBalancesProps> = ({ id }) => {
  const { formatMessage } = useIntl();

  const payload = useMemo(() => ({ id }), [id]);

  const { data: balances, isLoading } = useDispatchAction(getWalletBalancesAction, payload, []);
  const firstColumnKey = getFirstColumnKey(balances as Array<Record<string, unknown>>);

  return (
    <Box mt={4}>
      <Typography variant="subtitle1" fontWeight={500}>
        {formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.wallets.title',
          defaultMessage: 'Wallets (Available Currencies & Balances)',
        })}
      </Typography>
      {isLoading ? (
        <Skeleton />
      ) : (
        <StyledTableContainer>
          {!!(balances as unknown[]).length && firstColumnKey && (
            <Table>
              <TableHead>
                <StyledTableRow>
                  <StyledHeaderTableCell>
                    {formatMessage({
                      id: `configurations.company.paymentProviders.detailsModal.wallets.${firstColumnKey}`,
                      defaultMessage: firstColumnKey,
                    })}
                  </StyledHeaderTableCell>
                  <StyledHeaderTableCell align="right">
                    {formatMessage({
                      id: 'configurations.company.paymentProviders.detailsModal.wallets.balance',
                      defaultMessage: 'Balance',
                    })}
                  </StyledHeaderTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {(balances as Array<Record<string, unknown>>)?.map((row) => (
                  <TableRow key={(row?.id as string) || (row[firstColumnKey] as string)}>
                    <TableCell>{row[firstColumnKey] as React.ReactNode}</TableCell>
                    <TableCell align="right">{row.amount as React.ReactNode}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </StyledTableContainer>
      )}
    </Box>
  );
};

export default WalletBalances;
